import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { ArrowOutward } from '@mui/icons-material';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { safeWindowOpen } from '@utils/urlSanitization';
import { Box, Typography, useSnackbar, useTheme } from 'hephaestus/web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type IUrlOption = {
  link: string;
  label: TranslatedString;
};

type IQuickLinksOptions = {
  url1: IUrlOption;
  url2: IUrlOption;
  url3: IUrlOption;
  url4: IUrlOption;
  url5: IUrlOption;
  url6: IUrlOption;
};

const Link = ({ link, label }: IUrlOption) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Card
      sx={{
        flex: 1,
        maxHeight: 72,
        minWidth: 165,
        boxShadow: theme.elevation.low,
      }}
      hover
      onClick={() =>
        safeWindowOpen(link, {
          target: '_blank',
          onError: (errorMsg) => {
            enqueueSnackbar(errorMsg, { variant: 'error' });
          },
        })
      }
    >
      <CardContent sx={{ padding: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='bodyLarge'
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {translateBackend(label)}
          </Typography>
          <ArrowOutward sx={{ fontSize: 20 }} />
        </Box>
      </CardContent>
    </Card>
  );
};

const defaultLabel = getLanguageDefaults();

export const QuickLinks = ({
  options = {
    url1: {
      link: '',
      label: defaultLabel,
    },
    url2: {
      link: '',
      label: defaultLabel,
    },
    url3: {
      link: '',
      label: defaultLabel,
    },
    url4: {
      link: '',
      label: defaultLabel,
    },
    url5: {
      link: '',
      label: defaultLabel,
    },
    url6: {
      link: '',
      label: defaultLabel,
    },
  },
}: {
  options: IQuickLinksOptions;
}) => {
  const { url1, url2, url3, url4, url5, url6 } = options;
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const updateHasOverflow = () => {
      if (ref.current)
        setHasOverflow(
          (ref.current.scrollWidth ?? 0) > (ref.current.clientWidth ?? 0),
        );
    };
    updateHasOverflow();
    window.addEventListener('resize', updateHasOverflow);
    return () => window.removeEventListener('resize', updateHasOverflow);
  }, [ref]);

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        overflowX: 'auto',
        paddingBottom: 0.5,

        '&:hover': {
          paddingBottom: hasOverflow ? 0 : undefined,
        },

        '&::-webkit-scrollbar': {
          height: '4px',
          display: 'none',
        },

        '&:hover::-webkit-scrollbar': {
          display: 'block',
        },

        '&::-webkit-scrollbar-track': {
          backgroundColor: `${theme.color.onSurfaceVariant}33`,
          borderRadius: '4px',
        },

        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `${theme.color.onSurfaceVariant}4D`,
          borderRadius: '4px',
        },
      }}
    >
      {url1.link && <Link link={url1.link} label={url1.label} />}
      {url2.link && <Link link={url2.link} label={url2.label} />}
      {url3.link && <Link link={url3.link} label={url3.label} />}
      {url4.link && <Link link={url4.link} label={url4.label} />}
      {url5.link && <Link link={url5.link} label={url5.label} />}
      {url6.link && <Link link={url6.link} label={url6.label} />}
    </Box>
  );
};
